<template>
  <div>
    <a-card :bordered="false" title="结转参数设置">
      <a-tabs v-model="activeTab">
        <!-- ==================== 账套配置 ==================== -->
        <a-tab-pane tab="账套配置" key="set">
          <a-form layout="inline" style="margin-bottom: 12px;">
            <a-form-item label="账套">
              <a-select v-model="accountSetId" style="width: 260px" show-search option-filter-prop="children" @change="onAccountSetChange">
                <a-select-option v-for="s in accountSets" :key="s.id" :value="s.id">{{ s.name }}</a-select-option>
              </a-select>
            </a-form-item>
          </a-form>
          <a-alert type="info" show-icon style="margin-bottom: 12px;" message="结转参数为账套级配置：测算税率/附加率与取数科目、各卡片凭证模板行（多行，支持一借多贷/多借一贷）。建账时已从系统默认按账套会计准则复制，此后独立演化。" />

          <!-- 分组一：测算参数 -->
          <div v-for="g in groups" :key="g.title" style="margin-bottom: 8px;">
            <a-divider orientation="left">{{ g.title }}</a-divider>
            <a-row :gutter="16">
              <a-col v-for="f in g.fields" :key="f.key" :md="12" :sm="24" style="margin-bottom: 8px;">
                <span style="display:inline-block;width:200px;text-align:right;padding-right:8px;">{{ f.label }}：</span>
                <a-select
                  v-if="f.type === 'subject'"
                  v-model="form[f.key]"
                  style="width: calc(100% - 208px);min-width:180px;"
                  show-search
                  option-filter-prop="children"
                  allow-clear
                  placeholder="选择科目"
                >
                  <a-select-option v-for="s in leafSubjects" :key="s.subjectName" :value="s.subjectName">{{ s.subjectCode }} {{ s.subjectName }}</a-select-option>
                </a-select>
                <a-input-number
                  v-else
                  v-model="form[f.key]"
                  :min="0"
                  :max="100"
                  :step="0.01"
                  :formatter="percentFormatter"
                  :parser="percentParser"
                  style="width: calc(100% - 208px);min-width:180px;"
                  placeholder="百分比"
                />
              </a-col>
            </a-row>
          </div>
          <a-button type="primary" :loading="saving" @click="save">保存参数</a-button>

          <!-- 分组二：凭证模板 -->
          <a-divider orientation="left">凭证模板（结转卡片凭证行）</a-divider>
          <a-form layout="inline" style="margin-bottom: 8px;">
            <a-form-item label="结转卡片">
              <a-select v-model="templateCardKey" style="width: 240px" @change="pickTemplateCard">
                <a-select-option v-for="c in templateCards" :key="c.key" :value="c.key">{{ c.name }}</a-select-option>
              </a-select>
            </a-form-item>
            <a-button @click="addTemplateLine">加行</a-button>
            <a-button style="margin-left:8px;" :loading="savingTemplate" @click="saveTemplate">保存模板</a-button>
            <a-button style="margin-left:8px;" @click="resetTemplate">重置为系统默认</a-button>
          </a-form>
          <div style="color:#999;margin-bottom:8px;">「自动」行按测算键在结账向导中预填金额（可改）；「手工」行由用户在生成时填写。科目为编码优先解析、名称回退。</div>
          <div class="scroll-wrap" :style="{ maxHeight: scrollHeight + 'px', overflowY: 'auto' }">
          <table class="gl-tpl-table">
            <thead>
              <tr><th style="width:70px;">方向</th><th>科目</th><th>摘要</th><th style="width:70px;">来源</th><th style="width:60px;">操作</th></tr>
            </thead>
            <tbody>
              <tr v-for="(line, idx) in templateLines" :key="idx">
                <td>
                  <a-select v-model="line.direction" size="small" style="width:100%">
                    <a-select-option value="借">借</a-select-option>
                    <a-select-option value="贷">贷</a-select-option>
                  </a-select>
                </td>
                <td>
                  <a-select
                    v-model="line.subjectId"
                    size="small"
                    style="width:100%"
                    show-search
                    option-filter-prop="children"
                    placeholder="选择科目"
                  >
                    <a-select-option v-for="s in leafSubjects" :key="s.id" :value="s.id">{{ s.subjectCode }} {{ s.subjectName }}</a-select-option>
                  </a-select>
                </td>
                <td><a-input v-model="line.summary" size="small" placeholder="摘要" /></td>
                <td style="text-align:center;"><a-tag v-if="line.calc_key" color="blue">自动</a-tag><a-tag v-else>手工</a-tag></td>
                <td style="text-align:center;"><a-icon type="delete" style="color:#f5222d;cursor:pointer;" @click="removeTemplateLine(idx)" /></td>
              </tr>
            </tbody>
          </table>
          </div>
        </a-tab-pane>

        <!-- ==================== 系统默认配置（超管） ==================== -->
        <a-tab-pane v-if="isSuperAdmin" tab="系统默认配置" key="system">
          <a-alert type="warning" show-icon style="margin-bottom: 12px;" message="系统默认模板（tenant_id=0）仅超级管理员可维护：修改只影响此后新建的账套，存量账套不受影响（可在账套配置中主动「重置为系统默认」）。" />
          <a-form layout="inline" style="margin-bottom: 8px;">
            <a-form-item label="会计准则">
              <a-radio-group v-model="systemStandard" @change="loadSystemTemplate">
                <a-radio value="小企业会计准则">小企业会计准则</a-radio>
                <a-radio value="企业会计准则">企业会计准则</a-radio>
              </a-radio-group>
            </a-form-item>
          </a-form>
          <div v-for="g in groups" :key="g.title" style="margin-bottom: 8px;">
            <a-divider orientation="left">{{ g.title }}</a-divider>
            <a-row :gutter="16">
              <a-col v-for="f in g.fields" :key="f.key" :md="12" :sm="24" style="margin-bottom: 8px;">
                <span style="display:inline-block;width:200px;text-align:right;padding-right:8px;">{{ f.label }}：</span>
                <a-input v-model="systemForm[f.key]" style="width: calc(100% - 208px);min-width:180px;" :placeholder="f.type === 'subject' ? '科目名称' : '如 0.12'" />
              </a-col>
            </a-row>
          </div>
          <a-button type="primary" :loading="savingSystem" @click="saveSystem">保存参数</a-button>

          <a-divider orientation="left">凭证模板（系统默认）</a-divider>
          <a-form layout="inline" style="margin-bottom: 8px;">
            <a-form-item label="结转卡片">
              <a-select v-model="systemTemplateCardKey" style="width: 240px" @change="pickSystemTemplateCard">
                <a-select-option v-for="c in systemTemplateCards" :key="c.key" :value="c.key">{{ c.name }}</a-select-option>
              </a-select>
            </a-form-item>
            <a-button @click="addSystemTemplateLine">加行</a-button>
            <a-button style="margin-left:8px;" :loading="savingSystemTemplate" @click="saveSystemTemplate">保存模板</a-button>
          </a-form>
          <div class="scroll-wrap" :style="{ maxHeight: scrollHeight + 'px', overflowY: 'auto' }">
          <table class="gl-tpl-table">
            <thead>
              <tr><th style="width:70px;">方向</th><th style="width:120px;">科目编码</th><th style="width:200px;">科目名称</th><th>摘要</th><th style="width:70px;">来源</th><th style="width:60px;">操作</th></tr>
            </thead>
            <tbody>
              <tr v-for="(line, idx) in systemTemplateLines" :key="idx">
                <td>
                  <a-select v-model="line.direction" size="small" style="width:100%">
                    <a-select-option value="借">借</a-select-option>
                    <a-select-option value="贷">贷</a-select-option>
                  </a-select>
                </td>
                <td><a-input v-model="line.subject_code" size="small" placeholder="如 560201" /></td>
                <td><a-input v-model="line.subject_name" size="small" placeholder="如 工资" /></td>
                <td><a-input v-model="line.summary" size="small" placeholder="摘要" /></td>
                <td style="text-align:center;"><a-tag v-if="line.calc_key" color="blue">自动</a-tag><a-tag v-else>手工</a-tag></td>
                <td style="text-align:center;"><a-icon type="delete" style="color:#f5222d;cursor:pointer;" @click="systemTemplateLines.splice(idx, 1)" /></td>
              </tr>
            </tbody>
          </table>
          </div>
        </a-tab-pane>
      </a-tabs>
    </a-card>
  </div>
</template>

<script>
import { accountSetList, accountSetCurrent, subjectLeafList, carryConfigList, carryConfigSave, carryConfigReset, carryTemplateList, carryTemplateSave, carryTemplateReset } from './api'

// 参数分组（key 与后端 jsh_carry_config.config_key 白名单一致；科目行结构已移交 jsh_carry_template）
const GROUPS = [
  { title: '增值税测算（取数科目）', fields: [
    { key: 'vat_output_subject', label: '销项税额科目', type: 'subject' },
    { key: 'vat_input_subject', label: '进项税额科目', type: 'subject' }
  ] },
  { title: '税金及附加（附加率拆分）', fields: [
    { key: 'surcharge_city_rate', label: '城建税率', type: 'rate' },
    { key: 'surcharge_edu_rate', label: '教育费附加率', type: 'rate' },
    { key: 'surcharge_local_rate', label: '地方教育附加率', type: 'rate' }
  ] },
  { title: '所得税', fields: [
    { key: 'income_rate', label: '所得税率', type: 'rate' },
    { key: 'income_rate_small', label: '小微所得税率', type: 'rate' }
  ] }
]
const ALL_KEYS = [].concat(...GROUPS.map(g => g.fields.map(f => f.key)))
const RATE_KEYS = [].concat(...GROUPS.map(g => g.fields.filter(f => f.type === 'rate').map(f => f.key)))

export default {
  name: 'CarryConfigList',
  data() {
    return {
      activeTab: 'set',
      accountSets: [], accountSetId: null, leafSubjects: [],
      form: {}, systemForm: {},
      saving: false, savingSystem: false, isSuperAdmin: false,
      // 账套凭证模板
      templateCards: [], templateCardKey: null, templateLines: [], savingTemplate: false,
      // 系统默认凭证模板
      systemStandard: '小企业会计准则', systemTemplateCards: [], systemTemplateCardKey: null,
      systemTemplateLines: [], savingSystemTemplate: false,
      scrollHeight: Math.max(300, window.innerHeight - 420)
    }
  },
  computed: {
    groups() { return GROUPS }
  },
  created() {
    this.loadAccountSets()
  },
  methods: {
    // 附加率/税率后端存小数（0.12），界面按百分比展示（12%）
    percentFormatter(v) { return v == null || v === '' ? '' : (Number(v) * 100).toFixed(2).replace(/\.?0+$/, '') + '%' },
    percentParser(s) { const m = String(s).replace(/%/g, '').trim(); return m === '' ? '' : Number(m) / 100 },
    loadAccountSets() {
      accountSetList({ page: 1, pageSize: 100 }).then((res) => {
        if (res && res.success && res.data) {
          this.accountSets = res.data.records || []
          if (this.accountSets.length) {
            this.pickDefaultSet()
          } else {
            this.$message.warning('尚未建账')
          }
        }
      })
      this.probeSystemTab()
    },
    pickDefaultSet() {
      accountSetCurrent({}).then((res) => {
        const cur = res && res.success && res.data ? res.data : null
        const hit = cur && this.accountSets.find(s => s.id === cur.id)
        this.accountSetId = hit ? hit.id : this.accountSets[0].id
        this.onAccountSetChange()
      }).catch(() => { this.accountSetId = this.accountSets[0].id; this.onAccountSetChange() })
    },
    // 探测超管身份：能读系统默认模板（accountSetId=0）即超管，非超管后端报错则隐藏 Tab
    probeSystemTab() {
      carryConfigList({ accountSetId: 0 }).then((res) => {
        if (res && res.success && res.data) {
          this.isSuperAdmin = true
          this.systemForm = { ...res.data }
          this.loadSystemTemplate()
        } else {
          this.isSuperAdmin = false
        }
      }).catch(() => { this.isSuperAdmin = false })
    },
    onAccountSetChange() {
      this.loadConfig()
      this.loadTemplate()
    },
    loadConfig() {
      if (!this.accountSetId) return
      carryConfigList({ accountSetId: this.accountSetId }).then((res) => {
        if (res && res.success && res.data) {
          this.form = { ...res.data }
          this.loadSubjects()
        } else {
          this.$message.warning((res && res.message) || '读取配置失败')
        }
      })
    },
    loadSubjects() {
      subjectLeafList({ accountSetId: this.accountSetId }).then((res) => {
        if (res && res.success) this.leafSubjects = res.data || []
      })
    },
    save() {
      if (!this.accountSetId) return
      this.saving = true
      carryConfigSave({ accountSetId: this.accountSetId, values: this.form }).then((res) => {
        if (res && res.success) { this.$message.success('保存成功') } else { this.$message.error((res && res.message) || '保存失败') }
        this.saving = false
      }).catch(() => { this.saving = false; this.$message.error('保存失败') })
    },
    reset() {
      if (!this.accountSetId) return
      this.$confirm({
        title: '重置为系统默认',
        content: '将以系统默认模板覆盖本账套的税率与科目参数，确定继续？',
        onOk: () => {
          return carryConfigReset({ accountSetId: this.accountSetId }).then((res) => {
            if (res && res.success) { this.$message.success('已重置为系统默认'); this.loadConfig() } else { this.$message.error((res && res.message) || '重置失败') }
          })
        }
      })
    },
    // ===== 账套凭证模板 =====
    loadTemplate() {
      if (!this.accountSetId) return
      carryTemplateList({ accountSetId: this.accountSetId }).then((res) => {
        if (res && res.success && res.data) {
          this.templateCards = res.data.cards || []
          this.pickTemplateCard(this.templateCardKey || (this.templateCards[0] && this.templateCards[0].key))
        } else {
          this.$message.warning((res && res.message) || '读取凭证模板失败')
        }
      })
    },
    pickTemplateCard(key) {
      this.templateCardKey = key
      const card = this.templateCards.find(c => c.key === key)
      this.templateLines = ((card && card.lines) || []).map(l => ({
        direction: l.direction,
        subjectId: null,   // 由科目名匹配（模板行存 code+name，下拉初选用）
        subject_code: l.subject_code,
        subject_name: l.subject_name,
        summary: l.summary,
        calc_key: l.calc_key
      }))
      // 名称/编码匹配下拉项
      this.$nextTick(() => {
        this.templateLines.forEach(l => {
          const hit = this.leafSubjects.find(s => s.subjectCode === l.subject_code || s.subjectName === l.subject_name)
          if (hit) l.subjectId = hit.id
        })
      })
    },
    addTemplateLine() {
      this.templateLines.push({ direction: '借', subjectId: null, subject_code: '', subject_name: '', summary: '', calc_key: null })
    },
    removeTemplateLine(idx) { this.templateLines.splice(idx, 1) },
    saveTemplate() {
      if (!this.accountSetId || !this.templateCardKey) return
      if (this.templateLines.length < 2) { this.$message.warning('模板至少需要借贷各一行'); return }
      const lines = this.templateLines.map(l => {
        const hit = this.leafSubjects.find(s => s.id === l.subjectId)
        return {
          direction: l.direction,
          subject_code: hit ? hit.subjectCode : l.subject_code,
          subject_name: hit ? hit.subjectName : l.subject_name,
          summary: l.summary,
          calc_key: l.calc_key || null
        }
      })
      if (lines.some(l => !l.subject_code && !l.subject_name)) { this.$message.warning('存在未选择科目的模板行'); return }
      this.savingTemplate = true
      carryTemplateSave({ accountSetId: this.accountSetId, cardKey: this.templateCardKey, lines: lines }).then((res) => {
        if (res && res.success) { this.$message.success('模板保存成功') } else { this.$message.error((res && res.message) || '保存失败') }
        this.savingTemplate = false
      }).catch(() => { this.savingTemplate = false; this.$message.error('保存失败') })
    },
    resetTemplate() {
      if (!this.accountSetId) return
      this.$confirm({
        title: '重置凭证模板为系统默认',
        content: '将以系统默认模板（按本账套会计准则）覆盖全部卡片凭证行，确定继续？',
        onOk: () => {
          return carryTemplateReset({ accountSetId: this.accountSetId }).then((res) => {
            if (res && res.success) { this.$message.success('已重置为系统默认'); this.loadTemplate() } else { this.$message.error((res && res.message) || '重置失败') }
          })
        }
      })
    },
    // ===== 系统默认凭证模板 =====
    loadSystemTemplate() {
      carryTemplateList({ accountSetId: 0, standard: this.systemStandard }).then((res) => {
        if (res && res.success && res.data) {
          this.systemTemplateCards = res.data.cards || []
          this.pickSystemTemplateCard(this.systemTemplateCardKey || (this.systemTemplateCards[0] && this.systemTemplateCards[0].key))
        }
      })
    },
    pickSystemTemplateCard(key) {
      this.systemTemplateCardKey = key
      const card = this.systemTemplateCards.find(c => c.key === key)
      this.systemTemplateLines = ((card && card.lines) || []).map(l => ({
        direction: l.direction,
        subject_code: l.subject_code,
        subject_name: l.subject_name,
        summary: l.summary,
        calc_key: l.calc_key
      }))
    },
    addSystemTemplateLine() {
      this.systemTemplateLines.push({ direction: '借', subject_code: '', subject_name: '', summary: '', calc_key: null })
    },
    saveSystemTemplate() {
      if (!this.systemTemplateCardKey) return
      if (this.systemTemplateLines.length < 2) { this.$message.warning('模板至少需要借贷各一行'); return }
      const lines = this.systemTemplateLines.map(l => ({
        direction: l.direction,
        subject_code: l.subject_code,
        subject_name: l.subject_name,
        summary: l.summary,
        calc_key: l.calc_key || null
      }))
      if (lines.some(l => !l.subject_code && !l.subject_name)) { this.$message.warning('存在未填写科目的模板行'); return }
      this.savingSystemTemplate = true
      carryTemplateSave({ accountSetId: 0, standard: this.systemStandard, cardKey: this.systemTemplateCardKey, lines: lines }).then((res) => {
        if (res && res.success) { this.$message.success('模板保存成功') } else { this.$message.error((res && res.message) || '保存失败') }
        this.savingSystemTemplate = false
      }).catch(() => { this.savingSystemTemplate = false; this.$message.error('保存失败') })
    },
    saveSystem() {
      this.savingSystem = true
      const values = {}
      ALL_KEYS.forEach((k) => {
        const v = this.systemForm[k]
        values[k] = v == null ? '' : (RATE_KEYS.indexOf(k) >= 0 && v !== '' && !isNaN(Number(v)) && Number(v) > 1 ? String(Number(v) / 100) : String(v))
      })
      carryConfigSave({ accountSetId: 0, values: values }).then((res) => {
        if (res && res.success) { this.$message.success('保存成功') } else { this.$message.error((res && res.message) || '保存失败') }
        this.savingSystem = false
      }).catch(() => { this.savingSystem = false; this.$message.error('保存失败') })
    }
  }
}
</script>

<style scoped>
.scroll-wrap { overflow-x: auto; }
.gl-tpl-table { width: 100%; border-collapse: collapse; }
.gl-tpl-table th, .gl-tpl-table td { border: 1px solid #e8e8e8; padding: 4px 6px; font-size: 13px; }
.gl-tpl-table th { background: #fafafa; }
</style>
